import { useEffect, useRef } from 'react'
import { animate, useInView, useReducedMotion } from 'motion/react'
import { EASE } from './motion'

interface CountUpProps {
  value: number
  decimals?: number
  suffix?: string
  className?: string
}

// Counts up from 0 the first time it scrolls into view, then tweens from
// wherever it currently is whenever `value` changes (live scrape updates,
// discipline switches). Writes straight to the DOM node -- 60 setStates a
// second across a dozen counters would be pointless React work.
export function CountUp({ value, decimals = 0, suffix = '', className = '' }: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const current = useRef(0)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduce = useReducedMotion()

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const fmt = (n: number) =>
      n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix

    if (reduce) {
      current.current = value
      node.textContent = fmt(value)
      return
    }
    if (!inView) return

    const first = current.current === 0
    const controls = animate(current.current, value, {
      duration: first ? 2 : 0.9,
      ease: EASE,
      onUpdate: (v) => {
        current.current = v
        node.textContent = fmt(v)
      },
    })
    return () => controls.stop()
  }, [inView, reduce, value, decimals, suffix])

  return (
    <span ref={ref} className={`tabular-nums ${className}`}>
      {(0).toLocaleString('en-US', { minimumFractionDigits: decimals })}
      {suffix}
    </span>
  )
}
